import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  UseCase,
  TestTarget,
  TestTargetType,
  ActionDetail,
  StepAction,
  StepActionParameter,
  ParameterValue,
  ReviewStatus,
  UseCaseStep,
} from "../../types/TestTarget";
import { uuidv7 } from "uuidv7";
import {
  getActionConfig,
  hasTestTargetParameter,
  validateRequiredParameters,
} from "../../utils/actionConfig";
import {
  get_target_count_status,
  get_target_warning_messages,
  has_invalid_targets,
} from "../../utils/targetValidation";
import { is_feature_target } from "../../utils/viewItemTree";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DragIndicator as DragIndicatorIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { CircularProgress, Tooltip } from "@mui/material";
import {
  operationLogService,
  GenerateUsecasesFromTextRequest,
  OperationLogService,
  GenerateFromOperationLogResponse,
} from "../../services/operationLogService";
import { ApiConfig } from "../../config/apiConfig";
import {
  bulkUpdateTestCasesUsecase,
  bulkDeleteTestCasesUsecase,
  callBulkUpdateApi,
  callBulkDeleteApi,
  SSTFApiClient
} from "../../services/api";
import { BulkUpdateTestCasesUsecasePayload, BulkDeleteTestCasesUsecasePayload, IssueDetailValue } from "../../types/api";

// コンポーネントのインポート
import { BasicInfoTab } from "./BasicInfoTab";
import { ParameterSection } from "./ParameterSection";
import { PolicyTab } from "./PolicyTab";
import { StepEditor } from "./StepEditor";
import { IssuesTab } from "./IssuesTab";
import {
  extractParametersFromSteps,
  findTargetName,
  validateParameterName,
} from "./utils";
import TestTargetDetailView from "../TestTargetDetailView";
import { useToast } from "../../contexts/ToastContext";
import { OperationLogImportDialog } from "../OperationLogImportDialog";

// スタイルのインポート
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  Button,
  StepsSection,
  StepsList,
  DropZone,
  StepItem,
  DragHandle,
  StepHeader,
  StepNumber,
  StepContent,
  StepActions,
  IconButton,
  ViewValue,
  ViewStepItem,
  ViewStepHeader,
  ViewStepNumber,
  ViewStepContent,
  ViewStepField,
  InvalidTargetBadge,
  TargetWarningText,
  ChangeWarningText,
} from "./styles";

interface UseCaseEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  useCase: UseCase | null;
  onSave: (useCase: UseCase) => void;
  testTargets?: TestTarget[];
  currentViewId?: string;
  initialMode?: "view" | "edit" | "create";
  initialTab?: "basic" | "steps" | "policy" | "relatedTestCases" | "issues";
  initialPolicyId?: string;
  onCreateTestTarget?: (testTarget: any) => Promise<TestTarget | void>;
  onUpdateTestTarget?: (testTarget: any) => Promise<TestTarget | void>;
  defaultTestTargetData?: Partial<{
    type: TestTargetType;
    parentId: string;
  }>;
  // ユースケース生成用のプロパティ
  apiConfig?: ApiConfig;
  workspaceId?: string;
  projectId?: string;
  appSettings?: any;
  testcaseMappings?: import("../../services/api").TestcaseMappings | null;
  onUpdateTestcaseMappings?: (mappings: import("../../services/api").TestcaseMappings) => void;
  // #884: feature/procedure 編集では不要な UI を隠す（機能タブからの利用）。
  hidePolicyTab?: boolean;
  hideIssuesTab?: boolean;
  hideAiStepGenerate?: boolean;
  /** #884: ユーザー可視の呼称。機能タブからは「機能」を渡す。既定は従来どおり「ユースケース」。 */
  entityLabel?: string;
}

export const UseCaseEditModal: React.FC<UseCaseEditModalProps> = ({
  isOpen,
  onClose,
  useCase,
  onSave,
  testTargets = [],
  currentViewId,
  initialMode = "edit",
  initialTab = "basic",
  initialPolicyId,
  onCreateTestTarget,
  onUpdateTestTarget,
  defaultTestTargetData,
  apiConfig,
  workspaceId,
  projectId,
  appSettings,
  testcaseMappings,
  onUpdateTestcaseMappings,
  hidePolicyTab = false,
  hideIssuesTab = false,
  hideAiStepGenerate = false,
  entityLabel = "ユースケース",
}) => {
  const { showSuccess, showError, showInfo } = useToast();
  const [editedUseCase, setEditedUseCase] = useState<UseCase | null>(null);
  const [originalUseCase, setOriginalUseCase] = useState<UseCase | null>(null);
  const [mode, setMode] = useState<"view" | "edit" | "create">(initialMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  );
  // グローバルプルダウン状態管理（ステップ間での相互制御用）
  const [globalDropdownState, setGlobalDropdownState] = useState<{
    stepIndex: number | null;
    dropdownType: 'action' | 'parameter' | null;
    parameterName?: string;
  }>({ stepIndex: null, dropdownType: null });
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"basic" | "steps" | "policy" | "relatedTestCases" | "issues">(
    initialTab
  );

  // TestTargetDetailView のstate
  const [isTestTargetDetailOpen, setIsTestTargetDetailOpen] = useState(false);
  const [testTargetToEdit, setTestTargetToEdit] = useState<TestTarget | null>(
    null
  );
  const [pendingTestTargetCallback, setPendingTestTargetCallback] = useState<
    ((target: TestTarget) => void) | null
  >(null);

  // OperationLogImportDialogのstate
  const [isOperationLogDialogOpen, setIsOperationLogDialogOpen] = useState(false);

  // AIジョブモニタリング用のtrace_id, traceparent（ステップ生成時に設定、保存完了時に課金ログを出力）
  const [aiJobTraceId, setAiJobTraceId] = useState<string | null>(null);
  const [aiJobTraceparent, setAiJobTraceparent] = useState<string | null>(null);

  // 関連テストケースの状態
  const [relatedTestCases, setRelatedTestCases] = useState<
    Array<{
      testCaseId: string;
      testCaseName: string;
      testSuiteId: string;
      testSuiteName: string;
      metaId: string;
      usecaseOrder?: number;
      usecaseReason?: string;
      executionParameters?: Array<{
        name: string;
        value: string;
      }>;
    }>
  >([]);
  const [isLoadingTestCases, setIsLoadingTestCases] = useState(false);

  // 関連課題の状態
  const [relatedIssues, setRelatedIssues] = useState<IssueDetailValue[]>([]);
  const [isLoadingIssues, setIsLoadingIssues] = useState(false);
  const [issuesError, setIssuesError] = useState<string | null>(null);
  const [completedStatusIds, setCompletedStatusIds] = useState<Set<string>>(new Set());
  const [issueStatusMap, setIssueStatusMap] = useState<Map<string, { displayName: string; isCompleted: boolean; color?: string }>>(new Map());

  // テストケース一括操作用のstate
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateType, setBulkUpdateType] = useState<"usecase" | "parameter" | "ai_status">("usecase");
  const [bulkUpdateUsecaseId, setBulkUpdateUsecaseId] = useState<string>("");
  const [bulkUpdatePolicyId, setBulkUpdatePolicyId] = useState<string>("");
  const [availablePolicies, setAvailablePolicies] = useState<Array<{ policy_id: string; name: string }>>([]);
  const [bulkUpdateParameterName, setBulkUpdateParameterName] = useState<string>("");
  const [bulkUpdateParameterValue, setBulkUpdateParameterValue] = useState<string>("");
  const [isBulkOperationLoading, setIsBulkOperationLoading] = useState(false);

  // ローカルのTestTargetsリスト（新規作成されたTestTargetを含む）
  const [localTestTargets, setLocalTestTargets] =
    useState<TestTarget[]>(testTargets);

  // パラメータ名のバリデーションエラーを管理
  const [parameterErrors, setParameterErrors] = useState<Map<number, string>>(
    new Map()
  );
  
  // ポリシーパラメータ名のバリデーションエラーを管理
  const [policyParameterErrors, setPolicyParameterErrors] = useState<
    Map<string, string>
  >(new Map()); // key: "policyIndex-paramIndex"

  // ステップの必須パラメータバリデーションエラーを管理
  const [stepValidationErrors, setStepValidationErrors] = useState<Map<number, string>>(
    new Map()
  );

  // ポリシーステップの必須パラメータバリデーションエラーを管理
  const [policyStepValidationErrors, setPolicyStepValidationErrors] = useState<Map<string, string>>(
    new Map()
  ); // key: "policyIndex-stepIndex"

  // ステップ単位で不正なターゲットをチェックする関数
  // ステップのtest_targetsに含まれるtest_target_idが実際に存在するかチェック
  // NOTE: localTestTargetsを使用することで、新規作成されたターゲットも含めて判定する
  const hasInvalidTargetsInStep = useCallback(
    (step: UseCaseStep): boolean =>
      has_invalid_targets(step.action_detail?.test_targets, localTestTargets),
    [localTestTargets]
  );

  // testTargetsプロパティが変更されたときにlocalTestTargetsを更新
  // ただし、新規作成されたテストターゲットは保持する
  useEffect(() => {
    setLocalTestTargets(prevLocal => {
      // 新規作成されたテストターゲット（parentのtestTargetsに存在しないもの）を特定
      const newlyCreatedTargets = prevLocal.filter(localTarget => 
        !testTargets.some(parentTarget => parentTarget.id === localTarget.id)
      );
      
      // 親からのtestTargetsと新規作成されたテストターゲットをマージ
      return [...testTargets, ...newlyCreatedTargets];
    });
  }, [testTargets]);

  // 使用されているパラメータを抽出
  const usedParameters = useMemo(() => {
    if (!editedUseCase) return new Set<string>();
    return extractParametersFromSteps(editedUseCase.steps);
  }, [editedUseCase?.steps]);

  // 現在の画面のユースケースを取得（一括更新用）
  const allUseCases = useMemo(() => {
    const usecases: Array<{ usecase_id: string; name: string; viewId: string; policies: any[] }> = [];

    // currentViewIdが指定されている場合は、その画面のユースケースのみを取得
    if (currentViewId) {
      const currentView = localTestTargets.find(t => t.id === currentViewId);
      if (currentView && currentView.type === "view" && currentView.params && "usecases" in currentView.params) {
        const viewDetails = currentView.params as any;
        viewDetails.usecases?.forEach((uc: UseCase) => {
          usecases.push({
            usecase_id: uc.usecase_id,
            name: uc.name,
            viewId: currentView.id,
            policies: uc.policies || [],
          });
        });
      }
    }

    return usecases;
  }, [localTestTargets, currentViewId]);



  // ダイアログが開かれたときにinitialTabをリセット
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // 関連テストケースを取得
  useEffect(() => {
    if (isOpen && useCase?.usecase_id) {
      console.log(`🔄 関連テストケース取得開始: ユースケースID=${useCase.usecase_id}`);
      setIsLoadingTestCases(true);

      if (!testcaseMappings || testcaseMappings.usecaseToTestcases.size === 0) {
        console.log('⚠️ testcaseMappingsが空です');
        setRelatedTestCases([]);
        setIsLoadingTestCases(false);
        return;
      }

      // testcaseMappingsから該当するユースケースのテストケースを取得
      const relatedMappings = testcaseMappings.usecaseToTestcases.get(useCase.usecase_id) || [];
      console.log(`📊 取得したマッピング数: ${relatedMappings.length}`);

      if (relatedMappings.length > 0) {
        // マッピングをテストケース情報に変換
        const testCaseInfo = relatedMappings.map(mapping => {
          console.log('🔍 Mapping executionParameters:', mapping.executionParameters);
          return {
            testCaseId: mapping.testcaseId,
            testCaseName: mapping.testcaseName,
            testSuiteId: mapping.testsuiteId,
            testSuiteName: mapping.testsuiteName,
            metaId: mapping.metaId,
            usecaseOrder: mapping.usecaseOrder,
            usecaseReason: mapping.usecaseReason,
            executionParameters: mapping.executionParameters,
          };
        });

        console.log('📝 TestCaseInfo with params:', testCaseInfo);
        console.log(`✅ ${testCaseInfo.length}件の関連テストケースを設定`);
        setRelatedTestCases(testCaseInfo);
      } else {
        console.log('📭 関連テストケースなし');
        setRelatedTestCases([]);
      }

      setIsLoadingTestCases(false);
    }
  }, [isOpen, useCase?.usecase_id, testcaseMappings]);

  // テンプレートからステータス情報を抽出する関数
  const parseIssueStatusTemplate = useCallback((templateJson: string): {
    completedIds: Set<string>;
    statusMap: Map<string, { displayName: string; isCompleted: boolean; color?: string }>;
  } => {
    try {
      const template = JSON.parse(templateJson);
      const completedIds = new Set<string>();
      const statusMap = new Map<string, { displayName: string; isCompleted: boolean; color?: string }>();

      // attributes配列からissue_statusを探す
      const issueStatusAttr = template.attributes?.find(
        (attr: { id: string }) => attr.id === "issue_status"
      );

      if (issueStatusAttr?.data?.options) {
        for (const option of issueStatusAttr.data.options) {
          const isCompleted = option.issue_status_type?.value === 1;
          const displayName = option.display_name?.ja || option.display_name?.en || "-";

          // ステータスマップに追加
          statusMap.set(option.value, {
            displayName,
            isCompleted,
            color: option.color,
          });

          // issue_status_type.value が 1 なら完了
          if (isCompleted) {
            completedIds.add(option.value);
          }
        }
      }

      console.log("📋 完了ステータスID:", Array.from(completedIds));
      console.log("📋 ステータスマップ:", Object.fromEntries(statusMap));
      return { completedIds, statusMap };
    } catch (err) {
      console.error("❌ テンプレートの解析に失敗:", err);
      return { completedIds: new Set<string>(), statusMap: new Map() };
    }
  }, []);

  // 関連課題を取得する関数
  const fetchRelatedIssues = useCallback(async () => {
    if (!workspaceId || !projectId || !useCase?.usecase_id || !apiConfig) {
      console.log("⚠️ 関連課題取得: 必要な情報が不足しています", {
        workspaceId,
        projectId,
        usecaseId: useCase?.usecase_id,
        hasApiConfig: !!apiConfig,
      });
      return;
    }

    setIsLoadingIssues(true);
    setIssuesError(null);

    try {
      console.log("🔍 関連課題取得開始", {
        workspaceId,
        projectId,
        usecaseId: useCase.usecase_id,
      });

      const client = new SSTFApiClient(apiConfig.sstfBaseUrl);
      const response = await client.findIssues(
        workspaceId,
        projectId,
        useCase.usecase_id
      );

      console.log("✅ 関連課題取得成功", response);
      setRelatedIssues(response.issues || []);

      // テンプレートからステータス情報を抽出
      if (response.template) {
        const { completedIds, statusMap } = parseIssueStatusTemplate(response.template);
        setCompletedStatusIds(completedIds);
        setIssueStatusMap(statusMap);
      }
    } catch (err) {
      console.error("❌ 関連課題取得エラー", err);
      setIssuesError(
        err instanceof Error ? err.message : "課題の取得に失敗しました"
      );
    } finally {
      setIsLoadingIssues(false);
    }
  }, [workspaceId, projectId, useCase?.usecase_id, apiConfig, parseIssueStatusTemplate]);

  // 関連課題を取得（ダイアログを開いたタイミング）
  useEffect(() => {
    if (isOpen && useCase?.usecase_id) {
      fetchRelatedIssues();
    }
  }, [isOpen, useCase?.usecase_id, fetchRelatedIssues]);

  // ESCキーでダイアログを閉じる（編集モード時は無効）
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && !isTestTargetDetailOpen) {
        // イベント伝播を止めて、親のItemSpecificationPanelが閉じないようにする
        event.preventDefault();
        event.stopPropagation();

        if (mode === "view") {
          onClose();
        }
        // 編集モード時はダイアログを閉じない
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey, { capture: true });
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey, { capture: true });
    };
  }, [isOpen, onClose, isTestTargetDetailOpen, mode]);

  // ダイアログが閉じられたときにoriginalUseCaseとエラー状態をリセット
  useEffect(() => {
    if (!isOpen) {
      setOriginalUseCase(null);
      setParameterErrors(new Map());
      setPolicyParameterErrors(new Map());
      setStepValidationErrors(new Map());
      setPolicyStepValidationErrors(new Map());
    }
  }, [isOpen]);

  useEffect(() => {
    if (useCase) {
      // ステップのstep_idが空の場合はUUIDを生成
      const stepsWithIds = (useCase.steps || []).map((step) => ({
        ...step,
        step_id: step.step_id || uuidv7(),
      }));

      const processedUseCase = {
        ...useCase,
        title: useCase.title || useCase.name || "",
        usecase_id: useCase.usecase_id || "",
        name: useCase.title || "",
        steps: stepsWithIds,
        policies: useCase.policies || [],
        parameters: useCase.parameters || [],
        status: useCase.status || "draft",
      };

      setEditedUseCase(processedUseCase);
      // 編集モードで開かれた場合は、originalUseCaseを設定
      // 参照モードで開かれた場合は、originalUseCaseをnullにリセット
      if (mode === "edit" || initialMode === "edit") {
        setOriginalUseCase(JSON.parse(JSON.stringify(processedUseCase)));
      } else {
        setOriginalUseCase(null);
      }
      // エラー状態をクリア
      setParameterErrors(new Map());
      setPolicyParameterErrors(new Map());
      setStepValidationErrors(new Map());
      setPolicyStepValidationErrors(new Map());
    }
  }, [useCase, isOpen, mode, initialMode]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  // useCaseプロパティが外部から更新された時に最新データを再ロード
  useEffect(() => {
    if (useCase && mode === "view") {
      console.log("🔄 UseCaseEditModal: 参照モードでuseCase更新", useCase);
      // 参照モードで外部からuseCaseが更新された際に、最新のuseCaseデータで更新
      const stepsWithIds = (useCase.steps || []).map((step) => ({
        ...step,
        step_id: step.step_id || uuidv7(),
      }));

      setEditedUseCase({
        ...useCase,
        title: useCase.title || useCase.name || "",
        usecase_id: useCase.usecase_id || "",
        name: useCase.title || "",
        steps: stepsWithIds,
        policies: useCase.policies || [],
        parameters: useCase.parameters || [],
        status: useCase.status || "draft",
      });
    }
  }, [useCase]);

  const normalize_step_test_targets = (step: UseCaseStep): UseCaseStep => {
    const actionDetail = step.action_detail;
    const stepAction = actionDetail?.step_action;
    const action = stepAction?.action;
    if (!actionDetail || !stepAction || !action) {
      return step;
    }

    const actionConfig = getActionConfig(action);
    const testTargetParams =
      actionConfig?.parameters.filter((param) => param.type === "test_target") ||
      [];
    if (testTargetParams.length === 0) {
      return step;
    }

    const currentTargets = actionDetail.test_targets || [];
    if (currentTargets.length === 0) {
      return step;
    }

    const targetById: Map<string, (typeof currentTargets)[number]> = new Map();
    currentTargets.forEach((target) => {
      if (!target?.id || targetById.has(target.id)) {
        return;
      }
      targetById.set(target.id, target);
    });

    const actionParameters = stepAction.parameters || [];
    const expectedIds = testTargetParams.map((param, index) => {
      const paramValue =
        actionParameters.find((item) => item.key === param.name)?.value || "";
      return paramValue || String(index + 1);
    });
    const normalizedTargets = expectedIds.reduce(
      (acc, id) => {
        if (!id) {
          return acc;
        }
        const target = targetById.get(id);
        if (target) {
          acc.push(target);
        }
        return acc;
      },
      [] as typeof currentTargets
    );

    return {
      ...step,
      action_detail: {
        ...actionDetail,
        test_targets: normalizedTargets,
      },
    };
  };

  const normalize_usecase_test_targets = (useCaseData: UseCase): UseCase => {
    const normalizedSteps = (useCaseData.steps || []).map(
      normalize_step_test_targets
    );
    return {
      ...useCaseData,
      steps: normalizedSteps,
      status: useCaseData.status || "draft",
    };
  };

  const handleSave = async () => {
    if (!editedUseCase) return;

    // パラメータ名の重複チェック（保存前に再検証）
    const parameterValidationErrors: Map<number, string> = new Map();
    const policyValidationErrors: Map<string, string> = new Map();
    const stepValidationErrorsMap: Map<number, string> = new Map();
    const policyStepValidationErrorsMap: Map<string, string> = new Map();

    // UseCaseパラメータの重複チェック
    if (editedUseCase.parameters) {
      editedUseCase.parameters.forEach((param, index) => {
        if (param.name && param.name.trim()) {
          const validation = validateParameterName(
            editedUseCase.parameters || [],
            index,
            param.name
          );
          if (!validation.isValid) {
            parameterValidationErrors.set(index, validation.errorMessage || "");
          }
        }
      });
    }

    // Policyパラメータの重複チェック
    if (editedUseCase.policies) {
      editedUseCase.policies.forEach((policy, policyIndex) => {
        if (policy.parameters) {
          policy.parameters.forEach((param, paramIndex) => {
            if (param.name && param.name.trim()) {
              const validation = validateParameterName(
                policy.parameters || [],
                paramIndex,
                param.name
              );
              if (!validation.isValid) {
                const errorKey = `${policyIndex}-${paramIndex}`;
                policyValidationErrors.set(errorKey, validation.errorMessage || "");
              }
            }
          });
        }
      });
    }

    // ステップの必須パラメータチェック
    if (editedUseCase.steps) {
      editedUseCase.steps.forEach((step, stepIndex) => {
        const action = step.action_detail?.step_action?.action;
        if (action) {
          const parameters = step.action_detail?.step_action?.parameters || [];
          const validation = validateRequiredParameters(action, parameters);
          if (!validation.isValid) {
            stepValidationErrorsMap.set(stepIndex, "必須パラメータが入力されていません");
          }
        }
      });
    }

    // ポリシーステップの必須パラメータチェック
    if (editedUseCase.policies) {
      editedUseCase.policies.forEach((policy, policyIndex) => {
        // add_steps, update_steps, remove_stepsの各変更タイプをチェック
        const changeTypes: Array<"add_steps" | "update_steps" | "remove_steps"> = ["add_steps", "update_steps", "remove_steps"];
        changeTypes.forEach((changeType) => {
          const steps = policy.changes?.[changeType] || [];
          steps.forEach((step: any, stepIndex: number) => {
            const action = step.action_detail?.step_action?.action;
            if (action) {
              const parameters = step.action_detail?.step_action?.parameters || [];
              const validation = validateRequiredParameters(action, parameters);
              if (!validation.isValid) {
                const errorKey = `${policyIndex}-${changeType}-${stepIndex}`;
                policyStepValidationErrorsMap.set(errorKey, "必須パラメータが入力されていません");
              }
            }
          });
        });
      });
    }

    // エラーがある場合は保存を阻止
    if (parameterValidationErrors.size > 0 || policyValidationErrors.size > 0 || 
        stepValidationErrorsMap.size > 0 || policyStepValidationErrorsMap.size > 0) {
      setParameterErrors(parameterValidationErrors);
      setPolicyParameterErrors(policyValidationErrors);
      setStepValidationErrors(stepValidationErrorsMap);
      setPolicyStepValidationErrors(policyStepValidationErrorsMap);
      
      if (stepValidationErrorsMap.size > 0 || policyStepValidationErrorsMap.size > 0) {
        showError("必須パラメータが入力されていないステップがあります。");
      } else {
        showError("同名のパラメータが存在します。パラメータ名を修正してください。");
      }
      return;
    }

    const normalizedUseCase = normalize_usecase_test_targets(editedUseCase);

    setIsSaving(true);
    try {
      console.log("💾 Saving UseCase with policies:", normalizedUseCase.policies);
      await onSave(normalizedUseCase);

      // 課金ログの出力（trace_idがある場合のみ）
      if (aiJobTraceId && workspaceId && projectId) {
        try {
          console.log("📤 課金ログを送信:", aiJobTraceId, "traceparent:", aiJobTraceparent);
          await operationLogService.recordBillingLog({
            traceId: aiJobTraceId,
            projectId: projectId,
            workspaceId: workspaceId,
            billableUnitCount: 1, // ユースケースは1つ
            feature: "operationLog",
            message: `${entityLabel}の保存が完了しました`,
          });
          console.log("✅ 課金ログ出力成功");
          // 完了後はtrace_id, traceparentをクリア
          setAiJobTraceId(null);
          setAiJobTraceparent(null);
        } catch (billingError) {
          // 課金ログ出力が失敗してもユースケース保存は成功しているのでエラーはログのみ
          console.error("⚠️ 課金ログ出力に失敗:", billingError);
        }
      }

      // 保存成功後にエラー状態をクリア
      setParameterErrors(new Map());
      setPolicyParameterErrors(new Map());
      setStepValidationErrors(new Map());
      setPolicyStepValidationErrors(new Map());

      // トースト通知を表示
      if (initialMode === "create") {
        showSuccess(`${entityLabel}を作成しました`);
        // 作成後にoriginalUseCaseをリセット
        setOriginalUseCase(null);
        onClose();
      } else {
        showSuccess(`${entityLabel}を保存しました`);
        // 保存後にoriginalUseCaseをリセット
        setOriginalUseCase(null);
        // 保存後はモーダルを閉じて、次回開く時に正しいモードで開くようにする
        onClose();
      }
    } catch (error) {
      // 保存失敗時もエラーログを出力
      if (aiJobTraceId && workspaceId && projectId) {
        try {
          await operationLogService.recordBillingLog({
            traceId: aiJobTraceId,
            projectId: projectId,
            workspaceId: workspaceId,
            billableUnitCount: 0,
            feature: "operationLog",
            error: error instanceof Error ? error.message : "保存に失敗しました",
          });
          setAiJobTraceId(null);
          setAiJobTraceparent(null);
        } catch (billingError) {
          console.error("⚠️ エラーログ出力に失敗:", billingError);
        }
      }
      console.error("ユースケース保存エラー:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // 編集状態を判定する関数
  const hasChanges = (): boolean => {
    if (!originalUseCase || !editedUseCase) {
      return false;
    }
    
    // UseCaseオブジェクトを比較して変更があるかチェック
    const useCaseChanged = 
      JSON.stringify(editedUseCase) !== JSON.stringify(originalUseCase);
    
    return useCaseChanged;
  };

  const handleCancel = () => {
    if (initialMode === "create") {
      onClose();
    } else {
      // キャンセル時はモーダルを閉じて、次回開く時に正しいモードで開くようにする
      onClose();
    }
  };

  // AIでステップを生成（確認ダイアログを開く）
  const handleGenerateSteps = async () => {
    if (!editedUseCase || !currentViewId) {
      showError(`${entityLabel}情報または画面IDが不足しています`);
      return;
    }

    if (!apiConfig) {
      showError("認証情報が不足しています。API設定を確認してください。");
      return;
    }

    if (!workspaceId || !projectId) {
      showError("ワークスペースIDとプロジェクトIDが必要です");
      return;
    }

    // OperationLogImportDialogを開く（ユースケースモード）
    setIsOperationLogDialogOpen(true);
  };

  // OperationLogImportDialogから生成結果を受け取る
  const handleUseCaseStepsGenerated = async (result: GenerateFromOperationLogResponse) => {
    if (!editedUseCase) {
      return;
    }

    try {
      console.log("✅ ユースケースステップ生成成功:", result);

      // AIジョブモニタリング用のtrace_id, traceparentを保存（保存完了時に課金ログを出力するため）
      if (result.trace_id) {
        console.log("📝 AIジョブtrace_idを保存:", result.trace_id);
        setAiJobTraceId(result.trace_id);
      }
      if (result.traceparent) {
        console.log("📝 AIジョブtraceparentを保存:", result.traceparent);
        setAiJobTraceparent(result.traceparent);
      }

      // page_resultsから最初のページのユースケースを取得
      if (!result.page_results || result.page_results.length === 0) {
        throw new Error(`${entityLabel}の生成結果が空でした`);
      }

      const firstPage = result.page_results[0];
      const primaryUsecase = firstPage.usecases?.[0];
      if (!primaryUsecase) {
        throw new Error(`${entityLabel}の生成結果が空でした`);
      }

      const generatedSteps = (primaryUsecase.steps || []).map(
        (step: any, index: number) => {
          console.log(`🔍 Step ${index + 1}:`, step);
          return {
            step_id: uuidv7(),
            step: step.step,
            expect: step.expect || "",
            action_detail: step.action_detail,
            test_target_ids: step.test_target_ids || [],
          };
        }
      );

      const generatedParameters = (primaryUsecase.parameters || []).map(
        (param: any) => ({
          name: param.name,
          description: param.description || "",
          default_value: param.default_value || "",
        })
      );

      setEditedUseCase((prev) => {
        if (!prev) return null;

        return {
          ...prev,
          steps: generatedSteps,
          parameters: generatedParameters,
        };
      });

      if (result.generation_note) {
        const noteForToast = result.generation_note_link_url
          ? result.generation_note.replace(
              "{{MANUAL_LINK}}",
              result.generation_note_link_url
            )
          : result.generation_note;
        showInfo(noteForToast);
      }
      showSuccess(result.message || "ステップを生成しました");
      setActiveTab("steps"); // ステップタブに切り替え
    } catch (error: any) {
      console.error("❌ ステップ生成エラー:", error);
      showError(`ステップ生成に失敗しました: ${error.message}`);
    }
  };

  // 新規テストターゲット作成を開く
  const handleCreateNewTarget = (callback?: (newTarget: any) => void) => {
    // コールバック関数があればそれを設定、なければペンディングコールバックをnullに
    if (callback) {
      setPendingTestTargetCallback(() => callback);
    } else {
      setPendingTestTargetCallback(null);
    }
    
    // デフォルト値を設定した新しいテストターゲットを作成
    const defaultTarget = {
      id: "",
      name: "",
      type: defaultTestTargetData?.type || "item",
      description: "",
      displayOrder: 0,
      parentId: defaultTestTargetData?.parentId || currentViewId,
      params: {},
    };
    setTestTargetToEdit(defaultTarget as TestTarget);
    setIsTestTargetDetailOpen(true);
  };

  // TestTargetDetailView のハンドラー関数
  const handleTestTargetClose = () => {
    setIsTestTargetDetailOpen(false);
    setTestTargetToEdit(null);
    setPendingTestTargetCallback(null);
  };

  const handleTestTargetSave = async (targetData: any) => {
    console.log("🔍 handleTestTargetSave called with:", {
      targetData,
      testTargetToEdit,
      hasOnUpdateTestTarget: !!onUpdateTestTarget,
      hasOnCreateTestTarget: !!onCreateTestTarget
    });

    // 既存のテストターゲットの編集か、新規作成かを判断
    const isEditing = testTargetToEdit?.id && testTargetToEdit.id !== "";

    console.log("🔍 isEditing:", isEditing);

    if (isEditing && onUpdateTestTarget) {
      // 既存のテストターゲットの更新
      console.log("📝 Updating existing TestTarget:", testTargetToEdit.id);

      // targetDataにIDを確実に設定
      const targetDataWithId = {
        ...targetData,
        id: testTargetToEdit.id
      };
      console.log("🔍 targetDataWithId:", targetDataWithId);

      try {
        const updatedTarget = await onUpdateTestTarget(targetDataWithId);
        console.log("✅ TestTarget updated successfully:", updatedTarget);
        if (updatedTarget) {
          // localTestTargetsの該当項目を更新
          setLocalTestTargets(prev => prev.map(target =>
            target.id === updatedTarget.id ? updatedTarget : target
          ));
        }
      } catch (error) {
        console.error("❌ テストターゲット更新エラー:", error);
      }
    } else if (onCreateTestTarget) {
      // 新規テストターゲットの作成
      console.log("🆕 Creating new TestTarget");
      try {
        const newTarget = await onCreateTestTarget(targetData);
        console.log("✅ TestTarget created successfully:", newTarget);
        if (newTarget) {
          // 新しいテストターゲットをlocalTestTargetsに追加
          setLocalTestTargets(prev => [...prev, newTarget]);

          // ペンディング中のコールバックがあれば実行
          if (pendingTestTargetCallback) {
            pendingTestTargetCallback(newTarget);
          }
        }
      } catch (error) {
        console.error("❌ テストターゲット作成エラー:", error);
      }
    } else {
      console.warn("⚠️ No appropriate handler found for TestTarget save");
    }

    // モーダルを閉じる
    handleTestTargetClose();
  };

  const addStep = () => {
    const defaultAction = "click";
    const actionConfig = getActionConfig(defaultAction);
    const defaultParams: StepActionParameter[] =
      actionConfig?.parameters.map((p) => ({
        key: p.name,
        value: p.type === "test_target" ? "" : "",
      })) || [];

    setEditedUseCase((prev) =>
      prev
        ? {
            ...prev,
            steps: [
              ...prev.steps,
              {
                step_id: uuidv7(),
                step: "",
                expect: "",
                action_detail: {
                  step_action: {
                    action: defaultAction,
                    parameters: defaultParams,
                  },
                  test_targets: [],
                },
              },
            ],
          }
        : null
    );
  };

  const removeStep = (index: number) => {
    setEditedUseCase((prev) =>
      prev
        ? {
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index),
          }
        : null
    );
  };

  const updateStep = (
    index: number,
    field: string,
    value: string | string[]
  ) => {
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newSteps = [...prev.steps];
      newSteps[index] = { ...newSteps[index], [field]: value };
      return { ...prev, steps: newSteps };
    });
  };

  const updateStepAction = (index: number, action: string) => {
    const actionConfig = getActionConfig(action);
    const newParams: StepActionParameter[] =
      actionConfig?.parameters.map((p) => ({
        key: p.name,
        value: p.type === "test_target" ? "" : "",
      })) || [];

    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newSteps = [...prev.steps];
      const currentStep = newSteps[index];
      newSteps[index] = {
        ...currentStep,
        action_detail: {
          ...currentStep.action_detail,
          step_action: {
            action: action as StepAction["action"],
            parameters: newParams,
          },
          test_targets: currentStep.action_detail?.test_targets || [],
        },
      };
      return { ...prev, steps: newSteps };
    });
  };

  const updateActionParameter = async (
    stepIndex: number,
    paramKey: string,
    value: string
  ) => {
    // CREATE_NEWが選択された場合、新規テストターゲット作成モーダルを開く
    if (value === "CREATE_NEW") {
      // 新規作成モード用のコールバックを設定
      const callback = (newTarget: TestTarget) => {
        // 新しく作成したテストターゲットを自動選択
        if (newTarget && newTarget.id) {
          // 新規作成されたテストターゲットを直接使用してパラメータを更新
          setEditedUseCase((prev) => {
            if (!prev) return null;
            const newSteps = [...prev.steps];
            const currentStep = newSteps[stepIndex];
            const currentAction = currentStep.action_detail?.step_action;

            if (currentAction) {
              // パラメータごとに一意のIDを割り当てる
              const actionConfig = getActionConfig(currentAction.action);
              const testTargetParams = actionConfig?.parameters.filter(
                (p) => p.type === "test_target"
              ) || [];
              const paramIndex = testTargetParams.findIndex(
                (p) => p.name === paramKey
              );
              const uniqueId = String(paramIndex + 1); // "1", "2", "3"...
              
              // 既存パラメータを更新、または新規パラメータを追加
              let newParameters = [...currentAction.parameters];
              const existingParamIndex = newParameters.findIndex(p => p.key === paramKey);
              
              if (existingParamIndex >= 0) {
                // 既存パラメータを更新
                newParameters[existingParamIndex] = { ...newParameters[existingParamIndex], value: uniqueId };
              } else {
                // 新規パラメータを追加
                newParameters.push({ key: paramKey, value: uniqueId });
              }

              const targetEntry = {
                id: uniqueId,
                name: newTarget.name, // 新規作成されたテストターゲットの名前を直接使用
                test_target_id: newTarget.id, // 実際のTarget ID
              };

              // 既存のtest_targetsから、同じIDのエントリを探す
              const currentTestTargets = currentStep.action_detail?.test_targets || [];
              const existingTargetIndex = currentTestTargets.findIndex(
                (tt) => tt.id === uniqueId
              );
              
              let updatedTestTargets: typeof currentTestTargets;
              if (existingTargetIndex >= 0) {
                // 既存のエントリを更新（他のエントリは保持）
                updatedTestTargets = [...currentTestTargets];
                updatedTestTargets[existingTargetIndex] = targetEntry;
              } else {
                // 新しいエントリを追加（既存のエントリは保持）
                updatedTestTargets = [...currentTestTargets, targetEntry];
              }

              newSteps[stepIndex] = {
                ...currentStep,
                action_detail: {
                  ...currentStep.action_detail,
                  step_action: {
                    ...currentAction,
                    parameters: newParameters,
                  },
                  test_targets: updatedTestTargets,
                },
              };
            }

            return { ...prev, steps: newSteps };
          });
        }
      };
      setPendingTestTargetCallback(callback);
      
      // handleCreateNewTargetを使用してモーダルを開く
      handleCreateNewTarget();
      return;
    }

    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newSteps = [...prev.steps];
      const currentStep = newSteps[stepIndex];
      const currentAction = currentStep.action_detail?.step_action;

      if (currentAction) {
        // 既存パラメータを更新、または新規パラメータを追加
        let newParameters = [...currentAction.parameters];
        const existingParamIndex = newParameters.findIndex(p => p.key === paramKey);
        
        if (existingParamIndex >= 0) {
          // 既存パラメータを更新
          newParameters[existingParamIndex] = { ...newParameters[existingParamIndex], value };
        } else {
          // 新規パラメータを追加
          newParameters.push({ key: paramKey, value });
        }

        const actionConfig = getActionConfig(currentAction.action);
        const paramConfig = actionConfig?.parameters.find(
          (p) => p.name === paramKey
        );

        let updatedTestTargets = currentStep.action_detail?.test_targets || [];

        if (paramConfig?.type === "test_target") {
          if (value) {
            // 値が設定される場合
            const targetName = findTargetName(value, localTestTargets);
            
            // パラメータごとに一意のIDを割り当てる
            // test_target型のパラメータのリストを取得し、現在のパラメータのインデックスを取得
            const testTargetParams = actionConfig?.parameters.filter(
              (p) => p.type === "test_target"
            ) || [];
            const paramIndex = testTargetParams.findIndex(
              (p) => p.name === paramKey
            );
            const uniqueId = String(paramIndex + 1); // "1", "2", "3"...
            
            const targetEntry = {
              id: uniqueId,
              name: targetName,
              test_target_id: value, // 実際のTarget ID
            };

            // 既存のtest_targetsから、同じIDのエントリを探す
            const existingIndex = updatedTestTargets.findIndex(
              (tt) => tt.id === uniqueId
            );
            
            if (existingIndex >= 0) {
              // 既存のエントリを更新
              updatedTestTargets[existingIndex] = targetEntry;
            } else {
              // 新しいエントリを追加（既存のエントリは保持）
              updatedTestTargets = [...updatedTestTargets, targetEntry];
            }

            // parameterのvalueには一意のIDを保存（test_targetsのidと対応）
            newParameters = newParameters.map((p) =>
              p.key === paramKey ? { ...p, value: uniqueId } : p
            );
          } else {
            // 値がクリアされる場合（選択を解除）
            // パラメータごとに一意のIDを割り当てる
            const testTargetParams = actionConfig?.parameters.filter(
              (p) => p.type === "test_target"
            ) || [];
            const paramIndex = testTargetParams.findIndex(
              (p) => p.name === paramKey
            );
            const uniqueId = String(paramIndex + 1);
            
            // 該当するIDのエントリのみを削除（他のパラメータのエントリは保持）
            updatedTestTargets = updatedTestTargets.filter(
              (tt) => tt.id !== uniqueId
            );
            
            // parameterのvalueも空文字列に設定
            newParameters = newParameters.map((p) =>
              p.key === paramKey ? { ...p, value: "" } : p
            );
          }
        }

        newSteps[stepIndex] = {
          ...currentStep,
          action_detail: {
            ...currentStep.action_detail,
            step_action: {
              ...currentAction,
              parameters: newParameters,
            },
            test_targets: updatedTestTargets,
          },
        };
      }

      return { ...prev, steps: newSteps };
    });
  };

  // パラメータ関連の関数
  const addParameter = () => {
    setEditedUseCase((prev) =>
      prev
        ? {
            ...prev,
            parameters: [
              ...(prev.parameters || []),
              {
                name: "",
                default_value: "",
                description: "",
              },
            ],
          }
        : null
    );
  };

  const removeParameter = (index: number) => {
    setEditedUseCase((prev) =>
      prev
        ? {
            ...prev,
            parameters: prev.parameters?.filter((_, i) => i !== index) || [],
          }
        : null
    );
    // エラー状態も更新（削除されたパラメータのエラーを削除、インデックスを再計算）
    setParameterErrors((prevErrors) => {
      const newErrors = new Map<number, string>();
      prevErrors.forEach((error, errorIndex) => {
        if (errorIndex < index) {
          newErrors.set(errorIndex, error);
        } else if (errorIndex > index) {
          newErrors.set(errorIndex - 1, error);
        }
        // errorIndex === index の場合は削除
      });
      return newErrors;
    });
  };

  const updateParameter = (
    index: number,
    field: keyof ParameterValue,
    value: string
  ) => {
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newParameters = [...(prev.parameters || [])];
      newParameters[index] = { ...newParameters[index], [field]: value };
      
      // パラメータ名が更新された場合、重複チェックを実行
      if (field === "name") {
        const validation = validateParameterName(
          newParameters,
          index,
          value
        );
        
        setParameterErrors((prevErrors) => {
          const newErrors = new Map(prevErrors);
          if (validation.isValid) {
            newErrors.delete(index);
          } else {
            newErrors.set(index, validation.errorMessage || "");
          }
          return newErrors;
        });
      }
      
      return { ...prev, parameters: newParameters };
    });
  };

  // ポリシー関連の関数
  const addPolicy = () => {
    console.log("🔧 addPolicy called");
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newPolicy = {
        id: uuidv7(),
        policy_id: uuidv7(),
        name: "",
        description: "",
        reviewed: false,
        changes: {
          add_steps: [],
          update_steps: [],
          remove_steps: [],
        },
        parameters: [],
      };
      const updated = {
        ...prev,
        policies: [...(prev.policies || []), newPolicy],
      };
      console.log("🔧 Updated policies:", updated.policies);
      return updated;
    });
  };

  const removePolicy = (index: number) => {
    setEditedUseCase((prev) =>
      prev
        ? {
            ...prev,
            policies: prev.policies?.filter((_, i) => i !== index) || [],
          }
        : null
    );
  };

  const updatePolicy = (
    index: number,
    field: "name" | "description" | "status",
    value: string | ReviewStatus
  ) => {
    console.log(
      `🔧 updatePolicy called: index=${index}, field=${field}, value=${value}`
    );
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newPolicies = [...(prev.policies || [])];
      newPolicies[index] = { ...newPolicies[index], [field]: value };
      console.log(`🔧 Updated policy [${index}]:`, newPolicies[index]);
      return { ...prev, policies: newPolicies };
    });
  };

  const processingRequestsRef = useRef<Set<string>>(new Set());

  const addPolicyStep = (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps"
  ) => {
    const requestId = `${policyIndex}-${changeType}-${Date.now()}`;
    if (processingRequestsRef.current.has(requestId)) {
      console.log(`🔧 addPolicyStep SKIPPED: duplicate request ${requestId}`);
      return;
    }

    processingRequestsRef.current.add(requestId);
    console.log(`🔧 addPolicyStep START: ${requestId}`);

    setTimeout(() => {
      processingRequestsRef.current.delete(requestId);
    }, 100);

    setEditedUseCase((prev) => {
      if (!prev) {
        console.log(`🔧 addPolicyStep ABORTED: prev is null`);
        return null;
      }
      const newPolicies = [...(prev.policies || [])];
      const policy = newPolicies[policyIndex];

      if (!policy) {
        console.log(
          `🔧 addPolicyStep ABORTED: policy not found at index ${policyIndex}`
        );
        return prev;
      }

      const beforeCount = policy.changes[changeType].length;
      console.log(`🔧 Before adding: ${changeType} count = ${beforeCount}`);

      const lastStepId =
        beforeCount > 0
          ? policy.changes[changeType][beforeCount - 1].step_id
          : null;

      const newStepId = uuidv7();
      const newStep = {
        step_id: newStepId,
        step: changeType === "remove_steps" ? undefined : "",
        expect: changeType === "remove_steps" ? undefined : "",
        insert_position: changeType === "add_steps" ? "end" : undefined,
      };

      if (lastStepId && beforeCount > 0) {
        const timeDiff =
          parseInt(newStepId.substring(0, 12), 16) -
          parseInt(lastStepId.substring(0, 12), 16);
        if (timeDiff < 100) {
          console.log(`🔧 Duplicate add detected within 100ms, skipping`);
          return prev;
        }
      }

      console.log(`🔧 Adding new step to ${changeType}:`, newStep);
      policy.changes[changeType] = [...policy.changes[changeType], newStep];
      const afterCount = policy.changes[changeType].length;
      console.log(
        `🔧 After adding: ${changeType} count = ${afterCount} (should be ${
          beforeCount + 1
        })`
      );

      if (afterCount !== beforeCount + 1) {
        console.error(
          `🚨 UNEXPECTED COUNT CHANGE: expected ${
            beforeCount + 1
          }, got ${afterCount}`
        );
      }

      return { ...prev, policies: newPolicies };
    });

    console.log(`🔧 addPolicyStep END: ${requestId}`);
  };

  const removePolicyStep = (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps",
    stepIndex: number
  ) => {
    console.log(
      `🔧 removePolicyStep called: policyIndex=${policyIndex}, changeType=${changeType}, stepIndex=${stepIndex}`
    );
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newPolicies = [...(prev.policies || [])];
      const policy = newPolicies[policyIndex];

      console.log(
        `🔧 Before removal, ${changeType} count:`,
        policy.changes[changeType].length
      );
      policy.changes[changeType] = policy.changes[changeType].filter(
        (_, i) => i !== stepIndex
      );
      console.log(
        `🔧 After removal, ${changeType} count:`,
        policy.changes[changeType].length
      );
      return { ...prev, policies: newPolicies };
    });
  };

  const updatePolicyStep = (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps",
    stepIndex: number,
    field: string,
    value: string | ActionDetail
  ) => {
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newPolicies = [...(prev.policies || [])];
      const policy = newPolicies[policyIndex];

      policy.changes[changeType][stepIndex] = {
        ...policy.changes[changeType][stepIndex],
        [field]: value,
      };
      return { ...prev, policies: newPolicies };
    });
  };

  // ポリシーパラメータ関連の関数
  const addPolicyParameter = (policyIndex: number) => {
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newPolicies = [...(prev.policies || [])];
      const policy = newPolicies[policyIndex];
      
      policy.parameters = [
        ...(policy.parameters || []),
        {
          name: "",
          default_value: "",
          description: "",
        },
      ];
      
      return { ...prev, policies: newPolicies };
    });
  };

  const removePolicyParameter = (policyIndex: number, parameterIndex: number) => {
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newPolicies = [...(prev.policies || [])];
      const policy = newPolicies[policyIndex];
      
      policy.parameters = (policy.parameters || []).filter((_, i) => i !== parameterIndex);
      
      return { ...prev, policies: newPolicies };
    });
    // エラー状態も更新（削除されたパラメータのエラーを削除、インデックスを再計算）
    setPolicyParameterErrors((prevErrors) => {
      const newErrors = new Map<string, string>();
      prevErrors.forEach((error, errorKey) => {
        const [pIndex, paramIndex] = errorKey.split("-").map(Number);
        if (pIndex === policyIndex) {
          if (paramIndex < parameterIndex) {
            newErrors.set(errorKey, error);
          } else if (paramIndex > parameterIndex) {
            newErrors.set(`${pIndex}-${paramIndex - 1}`, error);
          }
          // paramIndex === parameterIndex の場合は削除
        } else {
          newErrors.set(errorKey, error);
        }
      });
      return newErrors;
    });
  };

  const updatePolicyParameter = (
    policyIndex: number,
    parameterIndex: number,
    field: keyof ParameterValue,
    value: string
  ) => {
    setEditedUseCase((prev) => {
      if (!prev) return null;
      const newPolicies = [...(prev.policies || [])];
      const policy = newPolicies[policyIndex];
      
      if (!policy.parameters) policy.parameters = [];
      const newParameters = [...policy.parameters];
      newParameters[parameterIndex] = {
        ...newParameters[parameterIndex],
        [field]: value,
      };
      
      // パラメータ名が更新された場合、重複チェックを実行
      if (field === "name") {
        const validation = validateParameterName(
          newParameters,
          parameterIndex,
          value
        );
        
        setPolicyParameterErrors((prevErrors) => {
          const newErrors = new Map(prevErrors);
          const errorKey = `${policyIndex}-${parameterIndex}`;
          if (validation.isValid) {
            newErrors.delete(errorKey);
          } else {
            newErrors.set(errorKey, validation.errorMessage || "");
          }
          return newErrors;
        });
      }
      
      policy.parameters = newParameters;
      return { ...prev, policies: newPolicies };
    });
  };


  // ドラッグ&ドロップのハンドラー
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";

    const stepElement = e.currentTarget.closest(
      "[data-step-item]"
    ) as HTMLElement;
    if (stepElement) {
      const clone = stepElement.cloneNode(true) as HTMLElement;
      clone.style.transform = "rotate(1deg)";
      clone.style.backgroundColor = "#fefefe";
      clone.style.border = "1px solid #cbd5e1";
      clone.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.15)";
      clone.style.width = stepElement.offsetWidth + "px";
      clone.style.opacity = "0.9";
      document.body.appendChild(clone);

      const handleElement = e.currentTarget as HTMLElement;
      const stepRect = stepElement.getBoundingClientRect();
      const handleRect = handleElement.getBoundingClientRect();
      const offsetX = handleRect.left - stepRect.left + handleRect.width / 2;
      const offsetY = 20;

      e.dataTransfer.setDragImage(clone, offsetX, offsetY);

      setTimeout(() => {
        if (document.body.contains(clone)) {
          document.body.removeChild(clone);
        }
      }, 0);
    }
  };

  const handleDropZoneDragOver = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null) return;

    if (draggedIndex === targetIndex || draggedIndex === targetIndex - 1) {
      setDropTargetIndex(null);
      return;
    }

    setDropTargetIndex(targetIndex);
  };

  const handleDropZoneDragLeave = (e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;

    if (!currentTarget.contains(relatedTarget)) {
      setDropTargetIndex(null);
    }
  };

  const handleDropZoneDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null) {
      return;
    }

    setEditedUseCase((prev) => {
      if (!prev) return null;

      const newSteps = [...prev.steps];
      const draggedStep = newSteps[draggedIndex];

      newSteps.splice(draggedIndex, 1);

      let insertIndex = targetIndex;
      if (draggedIndex < targetIndex) {
        insertIndex = targetIndex - 1;
      }

      newSteps.splice(insertIndex, 0, draggedStep);

      return { ...prev, steps: newSteps };
    });

    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleItemDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // テストケース一括操作用のハンドラー
  const handleSelectAllTestCases = (checked: boolean) => {
    if (checked) {
      setSelectedTestCaseIds(new Set(relatedTestCases.map(tc => tc.testCaseId)));
    } else {
      setSelectedTestCaseIds(new Set());
    }
  };

  const handleSelectTestCase = (testCaseId: string, checked: boolean) => {
    setSelectedTestCaseIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(testCaseId);
      } else {
        newSet.delete(testCaseId);
      }
      return newSet;
    });
  };

  const handleBulkDeleteTestCases = async () => {
    if (selectedTestCaseIds.size === 0) {
      showError("削除するテストケースを選択してください");
      return;
    }

    if (!apiConfig || !workspaceId || !projectId || !editedUseCase?.usecase_id) {
      showError("API設定が不足しています");
      return;
    }

    const usecaseName = editedUseCase?.name || "このユースケース";
    if (!window.confirm(
      `選択した${selectedTestCaseIds.size}件のテストケースから「${usecaseName}」の紐付けを削除しますか？\n\n` +
      `※テストケース自体は削除されず、usecase_policy_resultから対象ユースケースのみが削除されます。`
    )) {
      return;
    }

    setIsBulkOperationLoading(true);
    try {
      // サーバーサイドのバルク削除APIを呼び出し
      const payload = {
        workspace_id: workspaceId,
        project_id: projectId,
        testcase_ids: Array.from(selectedTestCaseIds),
        usecase_id: editedUseCase.usecase_id,
        sstf_base_url: apiConfig.sstfBaseUrl,
      };

      const result = await callBulkDeleteApi(payload);

      showSuccess(
        `${result.total_deleted}件のテストケースから「${usecaseName}」の紐付けを削除しました`
      );

      // 削除後、選択をクリア
      setSelectedTestCaseIds(new Set());
      setShowBulkActions(false);

      // 関連テストケースのリストを更新（削除されたテストケースを除外）
      setRelatedTestCases(prevTestCases =>
        prevTestCases.filter(tc => !selectedTestCaseIds.has(tc.testCaseId))
      );

      // testcaseMappings を更新（親コンポーネントに通知）
      if (testcaseMappings && onUpdateTestcaseMappings && editedUseCase?.usecase_id) {
        const updatedMappings = { ...testcaseMappings };

        // usecaseToTestcases から削除されたテストケースを除外
        const currentUsecaseMappings = updatedMappings.usecaseToTestcases.get(editedUseCase.usecase_id) || [];
        const filteredMappings = currentUsecaseMappings.filter(
          mapping => !selectedTestCaseIds.has(mapping.testcaseId)
        );
        updatedMappings.usecaseToTestcases.set(editedUseCase.usecase_id, filteredMappings);

        onUpdateTestcaseMappings(updatedMappings);
      }
    } catch (error) {
      console.error("一括削除エラー:", error);
      showError(`一括削除に失敗しました: ${error instanceof Error ? error.message : "不明なエラー"}`);
    } finally {
      setIsBulkOperationLoading(false);
    }
  };

  const handleOpenBulkUpdateDialog = () => {
    if (selectedTestCaseIds.size === 0) {
      showError("更新するテストケースを選択してください");
      return;
    }

    // 更新タイプを初期化
    setBulkUpdateType("usecase");
    setBulkUpdateParameterName("");
    setBulkUpdateParameterValue("");

    // 現在のユースケースを初期値として設定
    setBulkUpdateUsecaseId(editedUseCase?.usecase_id || "");
    setBulkUpdatePolicyId("");

    // 現在のユースケースのポリシーを設定
    if (editedUseCase?.policies && editedUseCase.policies.length > 0) {
      setAvailablePolicies(
        editedUseCase.policies.map(p => ({
          policy_id: p.policy_id,
          name: p.name,
        }))
      );
    } else {
      setAvailablePolicies([]);
    }

    setShowBulkUpdateDialog(true);
  };

  // ユースケース選択時にポリシーリストを更新
  const handleUsecaseChange = (usecaseId: string) => {
    setBulkUpdateUsecaseId(usecaseId);
    setBulkUpdatePolicyId(""); // ポリシーをリセット

    // 選択されたユースケースのポリシーを取得
    const selectedUseCase = allUseCases.find(uc => uc.usecase_id === usecaseId);
    if (selectedUseCase && selectedUseCase.policies.length > 0) {
      setAvailablePolicies(
        selectedUseCase.policies.map((p: any) => ({
          policy_id: p.policy_id,
          name: p.name,
        }))
      );
    } else {
      setAvailablePolicies([]);
    }
  };

  const handleBulkUpdateTestCases = async () => {
    if (selectedTestCaseIds.size === 0) {
      showError("更新するテストケースを選択してください");
      return;
    }

    // 更新タイプ別のバリデーション
    if (bulkUpdateType === "usecase") {
      if (!bulkUpdateUsecaseId) {
        showError("ユースケースを選択してください");
        return;
      }
    } else if (bulkUpdateType === "parameter") {
      if (!bulkUpdateUsecaseId) {
        showError("ユースケースを選択してください");
        return;
      }
      if (!bulkUpdateParameterName.trim()) {
        showError("パラメータ名を入力してください");
        return;
      }
      if (!bulkUpdateParameterValue.trim()) {
        showError("パラメータ値を入力してください");
        return;
      }
    }

    if (!apiConfig || !workspaceId || !projectId) {
      showError("API設定が不足しています");
      return;
    }

    setIsBulkOperationLoading(true);
    try {
      // サーバーサイドのバルク更新APIを呼び出し
      const payload = {
        workspace_id: workspaceId,
        project_id: projectId,
        testcase_ids: Array.from(selectedTestCaseIds),
        update_type: bulkUpdateType,
        source_usecase_id: editedUseCase?.usecase_id,
        target_usecase_id: bulkUpdateUsecaseId || undefined,
        policy_id: bulkUpdatePolicyId || undefined,
        parameter_name: bulkUpdateParameterName || undefined,
        parameter_value: bulkUpdateParameterValue || undefined,
        ai_status_id: bulkUpdateType === "ai_status" ? "a50ca36b-f022-5491-9bc7-c818bbf47381" : undefined,
        sstf_base_url: apiConfig.sstfBaseUrl,
      };

      const result = await callBulkUpdateApi(payload);

      // 更新タイプ別の成功メッセージと後処理
      if (bulkUpdateType === "usecase") {
        const selectedUseCase = allUseCases.find(uc => uc.usecase_id === bulkUpdateUsecaseId);
        const usecaseName = selectedUseCase?.name || "不明なユースケース";
        const policyName = bulkUpdatePolicyId
          ? availablePolicies.find(p => p.policy_id === bulkUpdatePolicyId)?.name || "不明なポリシー"
          : "ポリシーなし";

        showSuccess(
          `${result.total_updated}件のテストケースを ${usecaseName}（${policyName}）に変更しました`
        );

        // 関連テストケースのリストを更新
        // 更新されたユースケースが現在表示中のユースケースと異なる場合は、リストから削除
        if (bulkUpdateUsecaseId !== editedUseCase?.usecase_id) {
          setRelatedTestCases(prevTestCases =>
            prevTestCases.filter(tc => !selectedTestCaseIds.has(tc.testCaseId))
          );
        }

        // testcaseMappings を更新（親コンポーネントに通知）
        if (testcaseMappings && onUpdateTestcaseMappings && editedUseCase?.usecase_id) {
          const updatedMappings = { ...testcaseMappings };

          // 元のユースケースから削除
          const oldUsecaseMappings = updatedMappings.usecaseToTestcases.get(editedUseCase.usecase_id) || [];
          const remainingOldMappings = oldUsecaseMappings.filter(
            mapping => !selectedTestCaseIds.has(mapping.testcaseId)
          );
          updatedMappings.usecaseToTestcases.set(editedUseCase.usecase_id, remainingOldMappings);

          // 新しいユースケースに追加（異なるユースケースに変更した場合）
          if (bulkUpdateUsecaseId !== editedUseCase.usecase_id) {
            const newUsecaseMappings = updatedMappings.usecaseToTestcases.get(bulkUpdateUsecaseId) || [];
            console.log(`📋 移動先ユースケース (${bulkUpdateUsecaseId}) の既存マッピング数:`, newUsecaseMappings.length);

            const updatedTestCaseMappings = relatedTestCases
              .filter(tc => selectedTestCaseIds.has(tc.testCaseId))
              .map(tc => {
                const existingMapping = newUsecaseMappings.find(m => m.testcaseId === tc.testCaseId);
                return {
                  testcaseId: tc.testCaseId,
                  testcaseName: tc.testCaseName,
                  testsuiteId: tc.testSuiteId,
                  testsuiteName: tc.testSuiteName,
                  metaId: tc.metaId,
                  displayOrder: existingMapping?.displayOrder || 0,
                  stepIndex: existingMapping?.stepIndex,
                  stepDescription: existingMapping?.stepDescription,
                  stepAction: existingMapping?.stepAction,
                  usecaseOrder: tc.usecaseOrder,
                  usecaseReason: `一括更新により「${usecaseName}（${policyName}）」に変更`,
                  executionParameters: tc.executionParameters || [],
                };
              });

            console.log(`➕ 移動先に追加するテストケース数:`, updatedTestCaseMappings.length);
            console.log(`➕ 追加するテストケースID:`, updatedTestCaseMappings.map(m => m.testcaseId));

            updatedMappings.usecaseToTestcases.set(
              bulkUpdateUsecaseId,
              [...newUsecaseMappings, ...updatedTestCaseMappings]
            );

            console.log(`✅ 移動先ユースケース (${bulkUpdateUsecaseId}) の更新後マッピング数:`,
              updatedMappings.usecaseToTestcases.get(bulkUpdateUsecaseId)?.length);
          }

          console.log('📤 親コンポーネントにマッピング更新を通知');
          onUpdateTestcaseMappings(updatedMappings);
        }
      } else if (bulkUpdateType === "parameter") {
        showSuccess(
          `${result.total_updated}件のテストケースのパラメータ「${bulkUpdateParameterName}」を「${bulkUpdateParameterValue}」に変更しました`
        );

        // 関連テストケースのexecution_parametersを更新
        setRelatedTestCases(prevTestCases =>
          prevTestCases.map(tc => {
            if (selectedTestCaseIds.has(tc.testCaseId)) {
              const updatedParams = [...(tc.executionParameters || [])];
              const paramIndex = updatedParams.findIndex(p => p.name === bulkUpdateParameterName);
              if (paramIndex !== -1) {
                updatedParams[paramIndex] = { name: bulkUpdateParameterName, value: bulkUpdateParameterValue };
              } else {
                updatedParams.push({ name: bulkUpdateParameterName, value: bulkUpdateParameterValue });
              }
              return { ...tc, executionParameters: updatedParams };
            }
            return tc;
          })
        );

        // testcaseMappingsのexecution_parametersも更新
        if (testcaseMappings && onUpdateTestcaseMappings && editedUseCase?.usecase_id) {
          const updatedMappings = { ...testcaseMappings };
          const usecaseMappings = updatedMappings.usecaseToTestcases.get(editedUseCase.usecase_id) || [];
          const updatedUsecaseMappings = usecaseMappings.map(mapping => {
            if (selectedTestCaseIds.has(mapping.testcaseId)) {
              const updatedParams = [...(mapping.executionParameters || [])];
              const paramIndex = updatedParams.findIndex(p => p.name === bulkUpdateParameterName);
              if (paramIndex !== -1) {
                updatedParams[paramIndex] = { name: bulkUpdateParameterName, value: bulkUpdateParameterValue };
              } else {
                updatedParams.push({ name: bulkUpdateParameterName, value: bulkUpdateParameterValue });
              }
              return { ...mapping, executionParameters: updatedParams };
            }
            return mapping;
          });
          updatedMappings.usecaseToTestcases.set(editedUseCase.usecase_id, updatedUsecaseMappings);
          onUpdateTestcaseMappings(updatedMappings);
        }
      } else if (bulkUpdateType === "ai_status") {
        showSuccess(
          `${result.total_updated}件のテストケースのAIステータスを「テスト準備をAIに依頼」に変更しました`
        );
      }

      // 更新後、ダイアログを閉じて選択をクリア
      setShowBulkUpdateDialog(false);
      setSelectedTestCaseIds(new Set());
      setBulkUpdateUsecaseId("");
      setBulkUpdatePolicyId("");
      setBulkUpdateParameterName("");
      setBulkUpdateParameterValue("");
      setAvailablePolicies([]);
    } catch (error) {
      console.error("一括更新エラー:", error);
      showError(`一括更新に失敗しました: ${error instanceof Error ? error.message : "不明なエラー"}`);
    } finally {
      setIsBulkOperationLoading(false);
    }
  };

  // ドロップダウン外クリック時の処理
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownIndex(null);
    };

    if (openDropdownIndex !== null) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openDropdownIndex]);

  if (!editedUseCase) return null;

  // 確認ダイアログ用の情報を計算
  const currentView = localTestTargets.find(t => t.id === currentViewId);
  const viewName = currentView?.name || "不明な画面";
  const targetItemsCount = localTestTargets.filter(t =>
    t.type === "item" && t.parentId === currentViewId && !is_feature_target(t)
  ).length;

  return (
    <ModalOverlay
      isOpen={isOpen}
      onClick={(e) => e.target === e.currentTarget && mode === "view" && onClose()}
    >
      <ModalContent data-testid={mode === "edit" ? "usecase-edit-modal" : "usecase-view-modal"}>
        <ModalHeader>
          <ModalTitle>
            {mode === "view"
              ? `${entityLabel}の詳細`
              : mode === "create"
              ? `${entityLabel}作成`
              : `${entityLabel}編集`}
          </ModalTitle>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {mode === "view" && (
              <button
                onClick={() => {
                  // 編集モードに移行する際に現在の状態を保存
                  if (editedUseCase) {
                    setOriginalUseCase(JSON.parse(JSON.stringify(editedUseCase)));
                  }
                  setMode("edit");
                }}
                style={{
                  background: "#667eea",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                }}
              >
                編集
              </button>
            )}
            {mode === "view" && (
              <CloseButton onClick={onClose}>
                <CloseIcon />
              </CloseButton>
            )}
          </div>
        </ModalHeader>

        <TabContainer>
          <TabList>
            <Tab
              $isActive={activeTab === "basic"}
              onClick={() => setActiveTab("basic")}
            >
              基本情報
            </Tab>
            <Tab
              $isActive={activeTab === "steps"}
              onClick={() => setActiveTab("steps")}
            >
              ステップ・パラメータ ({editedUseCase.steps.length})
            </Tab>
            {!hidePolicyTab && (
              <Tab
                $isActive={activeTab === "policy"}
                onClick={() => setActiveTab("policy")}
              >
                ポリシー ({editedUseCase.policies?.length || 0})
              </Tab>
            )}
            <Tab
              $isActive={activeTab === "relatedTestCases"}
              onClick={() => setActiveTab("relatedTestCases")}
            >
              関連テストケース ({relatedTestCases.length})
            </Tab>
            {!hideIssuesTab && (
              <Tab
                $isActive={activeTab === "issues"}
                onClick={() => setActiveTab("issues")}
              >
                関連課題 ({relatedIssues.length})
              </Tab>
            )}
          </TabList>
        </TabContainer>

        <ModalBody>
          <TabContent>
            {activeTab === "basic" && (
              <BasicInfoTab
                editedUseCase={editedUseCase}
                setEditedUseCase={setEditedUseCase}
                mode={mode}
                onGenerateSteps={handleGenerateSteps}
                isGenerating={isGenerating}
                entityLabel={entityLabel}
              />
            )}

            {activeTab === "steps" && (
              <div style={{ display: "flex", gap: "16px", height: "100%" }}>
                {/* パラメータセクション */}
                <ParameterSection
                  parameters={editedUseCase.parameters}
                  usedParameters={usedParameters}
                  mode={mode}
                  onAddParameter={addParameter}
                  onRemoveParameter={removeParameter}
                  onUpdateParameter={updateParameter}
                  parameterErrors={parameterErrors}
                />

                {/* ステップセクション */}
                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <StepsSection style={{ flex: 1, minHeight: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                        paddingBottom: "4px",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          color: "#1e293b",
                        }}
                      >
                        ステップ ({editedUseCase.steps.length})
                      </h4>
                      {mode !== "view" && (
                        <div style={{ display: "flex", gap: "8px" }}>
                          {!hideAiStepGenerate && (
                          <Tooltip
                            title={
                              !editedUseCase.title
                                ? "基本情報タブでタイトルを入力してください"
                                : !editedUseCase.description
                                ? "基本情報タブで説明を入力してください"
                                : ""
                            }
                            arrow
                            placement="top"
                          >
                            <span>
                              <button
                                onClick={handleGenerateSteps}
                                disabled={isGenerating || !editedUseCase.title || !editedUseCase.description}
                                style={{
                                  padding: "4px 8px",
                                  fontSize: "0.7rem",
                                  background: isGenerating || !editedUseCase.title || !editedUseCase.description ? "#94a3b8" : "#8b5cf6",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: isGenerating || !editedUseCase.title || !editedUseCase.description ? "not-allowed" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {isGenerating ? (
                                  <>
                                    <CircularProgress size={12} style={{ color: "white" }} />
                                    生成中...
                                  </>
                                ) : (
                                  <>
                                    <AutoAwesomeIcon style={{ fontSize: "0.8rem" }} />
                                    AIで生成
                                  </>
                                )}
                              </button>
                            </span>
                          </Tooltip>
                          )}
                          <button
                            onClick={addStep}
                            style={{
                              padding: "2px 6px",
                              fontSize: "0.65rem",
                              background: "#667eea",
                              color: "white",
                            border: "none",
                            borderRadius: "3px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <AddIcon style={{ fontSize: "0.7rem" }} />
                          ステップを追加
                        </button>
                        </div>
                      )}
                    </div>

                    {mode !== "view" ? (
                      <StepsList>
                        {/* 最初のドロップゾーン */}
                        <DropZone
                          $isActive={dropTargetIndex === 0}
                          $isFirst={true}
                          onDragOver={(e) => handleDropZoneDragOver(e, 0)}
                          onDragLeave={handleDropZoneDragLeave}
                          onDrop={(e) => handleDropZoneDrop(e, 0)}
                        />

                        {editedUseCase.steps.map((step, index) => (
                          <React.Fragment key={index}>
                            <StepItem
                              data-step-item
                              onDragOver={handleItemDragOver}
                              $isDragging={draggedIndex === index}
                              style={{
                                zIndex: (() => {
                                  // 上のステップカードが下のステップカードよりも上のレイヤーに表示されるようにz-indexを設定
                                  // プルダウンが開いている場合は動的にz-indexを調整
                                  if (globalDropdownState.stepIndex === index && globalDropdownState.dropdownType) {
                                    // 現在のステップでプルダウンが開いている場合は非常に高いz-indexを設定
                                    // これにより、プルダウンを含むステップカード全体が他のステップカードよりも前面に表示される
                                    return 10000 + index;
                                  }
                                  // デフォルト: 上のステップカードが下のステップカードよりも上に表示されるように、indexに基づいてz-indexを設定
                                  return 100 + index;
                                })(),
                              }}
                            >
                              <DragHandle
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnd={handleDragEnd}
                              >
                                <DragIndicatorIcon
                                  style={{ fontSize: "1.2rem" }}
                                />
                              </DragHandle>

                              <StepHeader>
                                <StepNumber>{index + 1}</StepNumber>
                              </StepHeader>

                              <StepContent>
                                <StepEditor
                                  step={step}
                                  localTestTargets={localTestTargets}
                                  currentViewId={currentViewId}
                                  mode="edit"
                                  layout="compact"
                                  stepIndex={index}
                                  globalDropdownState={globalDropdownState}
                                  onGlobalDropdownChange={setGlobalDropdownState}
                                  onUpdateStep={(field, value) =>
                                    updateStep(index, field as string, value)
                                  }
                                  onUpdateAction={(action) =>
                                    updateStepAction(index, action)
                                  }
                                  onUpdateParameter={(paramKey, value) =>
                                    updateActionParameter(
                                      index,
                                      paramKey,
                                      value
                                    )
                                  }
                                  onCreateTestTarget={handleCreateNewTarget}
                                />
                              </StepContent>

                              <StepActions>
                                <IconButton
                                  className="delete"
                                  onClick={() => removeStep(index)}
                                  title="ステップを削除"
                                >
                                  <DeleteIcon style={{ fontSize: "1rem" }} />
                                </IconButton>
                              </StepActions>
                            </StepItem>
                            
                            {/* 必須パラメータバリデーションエラー表示 */}
                            {stepValidationErrors.has(index) && (
                              <div
                                style={{
                                  padding: "8px 12px",
                                  marginTop: "4px",
                                  marginBottom: "4px",
                                  backgroundColor: "#fee2e2",
                                  border: "1px solid #fca5a5",
                                  borderRadius: "4px",
                                  color: "#991b1b",
                                  fontSize: "0.75rem",
                                }}
                              >
                                必須パラメータが入力されていません
                              </div>
                            )}

                            {/* 各ステップの後のドロップゾーン */}
                            <DropZone
                              $isActive={dropTargetIndex === index + 1}
                              onDragOver={(e) =>
                                handleDropZoneDragOver(e, index + 1)
                              }
                              onDragLeave={handleDropZoneDragLeave}
                              onDrop={(e) => handleDropZoneDrop(e, index + 1)}
                            />
                          </React.Fragment>
                        ))}
                      </StepsList>
                    ) : (
                      <div
                        style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}
                      >
                        {editedUseCase.steps.length === 0 ? (
                          <ViewValue
                            style={{
                              textAlign: "center",
                              color: "#9ca3af",
                              fontStyle: "italic",
                            }}
                          >
                            ステップが登録されていません
                          </ViewValue>
                        ) : (
                          editedUseCase.steps.map((step, index) => {
                            const count_status = get_target_count_status(
                              step.action_detail?.step_action,
                              step.action_detail?.test_targets
                            );
                            const has_target_count_mismatch =
                              count_status?.has_mismatch ?? false;
                            const target_warning_messages =
                              get_target_warning_messages(
                                step.action_detail?.step_action,
                                step.action_detail?.test_targets,
                                localTestTargets
                              );

                            return (
                              <ViewStepItem key={index}>
                              <ViewStepHeader>
                                <ViewStepNumber>{index + 1}</ViewStepNumber>
                              </ViewStepHeader>
                              <ViewStepContent>
                                <ViewStepField>
                                  <strong>手順</strong>
                                  <span
                                    style={{
                                      color: step.step ? "inherit" : "#d1d5db",
                                      fontStyle: step.step
                                        ? "normal"
                                        : "italic",
                                    }}
                                  >
                                    {step.step || "手順が入力されていません"}
                                  </span>
                                  {step.action_detail?.step_action && (
                                    <div
                                      style={{
                                        fontSize: "0.7rem",
                                        color: "#6b7280",
                                        marginTop: "2px",
                                      }}
                                    >
                                      アクション:{" "}
                                      {step.action_detail.step_action.action}
                                      {step.action_detail.step_action.parameters
                                        .length > 0 && (
                                        <span>
                                          {" "}
                                          (
                                          {
                                            step.action_detail.step_action
                                              .parameters.length
                                          }
                                          個のパラメータ)
                                        </span>
                                      )}
                                      {step.action_detail.step_action.action !==
                                        "verify" &&
                                        hasTestTargetParameter(
                                          step.action_detail.step_action.action
                                        ) &&
                                        (!step.action_detail.test_targets ||
                                          step.action_detail.test_targets
                                            .length === 0) && (
                                          <span style={{ marginLeft: "8px" }}>
                                            <InvalidTargetBadge>
                                              ターゲット未設定
                                            </InvalidTargetBadge>
                                          </span>
                                        )}
                                      {(hasInvalidTargetsInStep(step) ||
                                        has_target_count_mismatch) && (
                                        <span style={{ marginLeft: "8px" }}>
                                          <InvalidTargetBadge>
                                            不正なターゲット
                                          </InvalidTargetBadge>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </ViewStepField>
                                <ViewStepField>
                                  <strong>期待結果</strong>
                                  <span
                                    style={{
                                      color: step.expect
                                        ? "inherit"
                                        : "#d1d5db",
                                      fontStyle: step.expect
                                        ? "normal"
                                        : "italic",
                                    }}
                                  >
                                    {step.expect ||
                                      "期待結果が入力されていません"}
                                  </span>
                                </ViewStepField>
                                <ViewStepField>
                                  <strong>対象</strong>
                                  <div
                                    style={{
                                      color:
                                        step.action_detail?.test_targets &&
                                        step.action_detail.test_targets.length >
                                          0
                                          ? "inherit"
                                          : "#d1d5db",
                                      fontStyle:
                                        step.action_detail?.test_targets &&
                                        step.action_detail.test_targets.length >
                                          0
                                          ? "normal"
                                          : "italic",
                                    }}
                                  >
                                    {step.action_detail?.test_targets &&
                                    step.action_detail.test_targets.length > 0
                                      ? step.action_detail.test_targets.map(
                                          (tt, idx) => {
                                            // tt.nameがIDになっている場合は、localTestTargetsから名前を再取得
                                            // NOTE: 新規作成されたターゲットの場合、tt.nameがIDのままになっている可能性がある
                                            const getDisplayName = (): string => {
                                              if (!tt.test_target_id) {
                                                return tt.name;
                                              }
                                              
                                              // localTestTargetsからターゲットを探す（再帰的に検索）
                                              const findTarget = (
                                                targets: TestTarget[]
                                              ): TestTarget | null => {
                                                for (const target of targets) {
                                                  if (target.id === tt.test_target_id) {
                                                    return target;
                                                  }
                                                  if (target.children) {
                                                    const found = findTarget(target.children);
                                                    if (found) return found;
                                                  }
                                                }
                                                return null;
                                              };
                                              
                                              const target = findTarget(localTestTargets);
                                              // 見つかった場合は名前を返し、見つからない場合はtt.nameを使用
                                              return target?.name || tt.name;
                                            };
                                            
                                            const displayName = getDisplayName();

                                            return (
                                              <span
                                                key={`${tt.test_target_id}-${idx}`}
                                              >
                                                <button
                                                  onClick={() => {
                                                    // test_target_idから実際のTestTargetを探す
                                                    const targetId =
                                                      tt.test_target_id;
                                                    const findTarget = (
                                                      targets: TestTarget[]
                                                    ): TestTarget | null => {
                                                      for (const target of targets) {
                                                        if (
                                                          target.id === targetId
                                                        )
                                                          return target;
                                                        if (target.children) {
                                                          const found =
                                                            findTarget(
                                                              target.children
                                                            );
                                                          if (found) return found;
                                                        }
                                                      }
                                                      return null;
                                                    };
                                                    const targetToView =
                                                      findTarget(
                                                        localTestTargets
                                                      );
                                                    if (targetToView) {
                                                      setTestTargetToEdit(
                                                        targetToView
                                                      );
                                                      setIsTestTargetDetailOpen(
                                                        true
                                                      );
                                                    }
                                                  }}
                                                  style={{
                                                    background: "none",
                                                    border: "none",
                                                    color: "#2563eb",
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                    padding: 0,
                                                    fontSize: "inherit",
                                                    fontFamily: "inherit",
                                                  }}
                                                  onMouseOver={(e) => {
                                                    e.currentTarget.style.color =
                                                      "#1d4ed8";
                                                  }}
                                                  onMouseOut={(e) => {
                                                    e.currentTarget.style.color =
                                                      "#2563eb";
                                                  }}
                                                >
                                                  {displayName}
                                                </button>
                                                {idx <
                                                (step.action_detail?.test_targets
                                                  ?.length || 0) -
                                                  1
                                                  ? ", "
                                                  : ""}
                                              </span>
                                            );
                                          }
                                        )
                                      : "対象が選択されていません"}
                                    {target_warning_messages.length > 0 && (
                                      <TargetWarningText>
                                        {target_warning_messages.map(
                                          (message, warning_idx) => (
                                            <div
                                              key={`${index}-target-warning-${warning_idx}`}
                                            >
                                              {message}
                                            </div>
                                          )
                                        )}
                                      </TargetWarningText>
                                    )}
                                  </div>
                                </ViewStepField>
                              </ViewStepContent>
                            </ViewStepItem>
                            );
                          })
                        )}
                      </div>
                    )}
                  </StepsSection>
                </div>
              </div>
            )}

            {activeTab === "policy" && (
              <PolicyTab
                editedUseCase={editedUseCase}
                setEditedUseCase={setEditedUseCase}
                mode={mode}
                localTestTargets={localTestTargets}
                currentViewId={currentViewId}
                policyStepValidationErrors={policyStepValidationErrors}
                initialPolicyId={initialPolicyId}
                onAddPolicy={addPolicy}
                onRemovePolicy={removePolicy}
                onUpdatePolicy={updatePolicy}
                onAddPolicyStep={addPolicyStep}
                onRemovePolicyStep={removePolicyStep}
                onUpdatePolicyStep={updatePolicyStep}
                onCreateTestTarget={handleCreateNewTarget}
                onAddPolicyParameter={addPolicyParameter}
                onRemovePolicyParameter={removePolicyParameter}
                onUpdatePolicyParameter={updatePolicyParameter}
                policyParameterErrors={policyParameterErrors}
              />
            )}

            {activeTab === "relatedTestCases" && (
              <div>
                {isLoadingTestCases ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                    関連テストケースを取得中...
                  </div>
                ) : relatedTestCases.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                    この{entityLabel}を使用しているテストケースは見つかりませんでした。
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        marginBottom: "12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                        この{entityLabel}を使用している{" "}
                        <strong>{relatedTestCases.length}</strong>{" "}
                        件のテストケースが見つかりました
                        {mode === "edit" && selectedTestCaseIds.size > 0 && (
                          <span style={{ marginLeft: "8px", color: "#3b82f6", fontWeight: 600 }}>
                            ({selectedTestCaseIds.size}件選択中)
                          </span>
                        )}
                      </div>
                      {mode === "edit" && (
                        <div style={{ display: "flex", gap: "8px" }}>
                          {selectedTestCaseIds.size > 0 && (
                            <>
                              <button
                                onClick={handleOpenBulkUpdateDialog}
                                disabled={isBulkOperationLoading}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: "0.75rem",
                                  background: isBulkOperationLoading ? "#9ca3af" : "#3b82f6",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: isBulkOperationLoading ? "not-allowed" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  opacity: isBulkOperationLoading ? 0.6 : 1,
                                }}
                              >
                                {isBulkOperationLoading ? (
                                  <CircularProgress size={12} style={{ color: "white" }} />
                                ) : (
                                  <EditIcon style={{ fontSize: "0.9rem" }} />
                                )}
                                一括更新
                              </button>
                              <button
                                onClick={handleBulkDeleteTestCases}
                                disabled={isBulkOperationLoading}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: "0.75rem",
                                  background: isBulkOperationLoading ? "#9ca3af" : "#ef4444",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: isBulkOperationLoading ? "not-allowed" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  opacity: isBulkOperationLoading ? 0.6 : 1,
                                }}
                              >
                                {isBulkOperationLoading ? (
                                  <CircularProgress size={12} style={{ color: "white" }} />
                                ) : (
                                  <DeleteIcon style={{ fontSize: "0.9rem" }} />
                                )}
                                一括削除
                              </button>
                              <button
                                onClick={() => setSelectedTestCaseIds(new Set())}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: "0.75rem",
                                  background: "#6b7280",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                }}
                              >
                                選択解除
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "0.8rem",
                        marginTop: "8px",
                      }}
                    >
                      <thead>
                        <tr>
                          {mode === "edit" && (
                            <th
                              style={{
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #e5e7eb",
                                padding: "8px 12px",
                                textAlign: "center",
                                fontWeight: 600,
                                color: "#374151",
                                fontSize: "0.75rem",
                                width: "3%",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTestCaseIds.size === relatedTestCases.length && relatedTestCases.length > 0}
                                onChange={(e) => handleSelectAllTestCases(e.target.checked)}
                                style={{ cursor: "pointer" }}
                              />
                            </th>
                          )}
                          <th
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e5e7eb",
                              padding: "8px 12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "0.75rem",
                              width: "12%",
                            }}
                          >
                            テストスイート
                          </th>
                          <th
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e5e7eb",
                              padding: "8px 12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "0.75rem",
                              width: "20%",
                            }}
                          >
                            テストケース名
                          </th>
                          <th
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e5e7eb",
                              padding: "8px 12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "0.75rem",
                              width: "8%",
                            }}
                          >
                            ID
                          </th>
                          <th
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e5e7eb",
                              padding: "8px 12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "0.75rem",
                              width: "35%",
                            }}
                          >
                            {entityLabel}情報
                          </th>
                          <th
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e5e7eb",
                              padding: "8px 12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#374151",
                              fontSize: "0.75rem",
                              width: "25%",
                            }}
                          >
                            パラメータ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatedTestCases.map((testCase, index) => {
                          // テストスイートへのリンクを生成
                          const testsuiteUrl = workspaceId && projectId && apiConfig?.webBaseUrl
                            ? `${apiConfig.webBaseUrl}/${workspaceId}/${projectId}/tests/${testCase.testSuiteId}/testcases`
                            : null;

                          return (
                            <tr
                              key={`${testCase.testCaseId}-${index}`}
                              style={{
                                borderBottom: "1px solid #e5e7eb",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                              }}
                            >
                              {mode === "edit" && (
                                <td
                                  style={{
                                    border: "1px solid #e5e7eb",
                                    padding: "8px 12px",
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedTestCaseIds.has(testCase.testCaseId)}
                                    onChange={(e) => handleSelectTestCase(testCase.testCaseId, e.target.checked)}
                                    style={{ cursor: "pointer" }}
                                  />
                                </td>
                              )}
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "6px 12px",
                                  verticalAlign: "top",
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {testsuiteUrl ? (
                                  <a
                                    href={testsuiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#3b82f6",
                                      textDecoration: "none",
                                      fontSize: "0.75rem",
                                      fontWeight: "500",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.textDecoration = "underline";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.textDecoration = "none";
                                    }}
                                  >
                                    {testCase.testSuiteName}
                                  </a>
                                ) : (
                                  <div style={{ fontSize: "0.75rem" }}>
                                    {testCase.testSuiteName}
                                  </div>
                                )}
                                <div style={{ fontSize: "0.65rem", color: "#6b7280", marginTop: "2px" }}>
                                  ID: {testCase.testSuiteId}
                                </div>
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "6px 12px",
                                  verticalAlign: "top",
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                }}
                              >
                                <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                                  {testCase.testCaseName}
                                </div>
                                <div style={{ fontSize: "0.65rem", color: "#6b7280" }}>
                                  ID: {testCase.testCaseId}
                                </div>
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "6px 12px",
                                  verticalAlign: "top",
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                }}
                              >
                                <div style={{ fontSize: "0.7rem", color: "#6b7280", fontFamily: "monospace" }}>
                                  {testCase.metaId}
                                </div>
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "6px 12px",
                                  verticalAlign: "top",
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {testCase.usecaseOrder !== undefined ? (
                                  <div>
                                    <div style={{ fontSize: "0.75rem", fontWeight: "600", color: "#3b82f6", marginBottom: "4px" }}>
                                      {testCase.usecaseOrder}番目の{entityLabel}
                                    </div>
                                    {testCase.usecaseReason && (
                                      <div style={{ fontSize: "0.7rem", color: "#6b7280", lineHeight: "1.4" }}>
                                        {testCase.usecaseReason}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "6px 12px",
                                  verticalAlign: "top",
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {testCase.executionParameters && testCase.executionParameters.length > 0 ? (
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    <thead>
                                      <tr>
                                        <th
                                          style={{
                                            backgroundColor: "#f9fafb",
                                            border: "1px solid #e5e7eb",
                                            padding: "4px 6px",
                                            textAlign: "left",
                                            fontWeight: 600,
                                            color: "#374151",
                                            fontSize: "0.65rem",
                                            width: "40%",
                                          }}
                                        >
                                          パラメータ名
                                        </th>
                                        <th
                                          style={{
                                            backgroundColor: "#f9fafb",
                                            border: "1px solid #e5e7eb",
                                            padding: "4px 6px",
                                            textAlign: "left",
                                            fontWeight: 600,
                                            color: "#374151",
                                            fontSize: "0.65rem",
                                            width: "60%",
                                          }}
                                        >
                                          値
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {testCase.executionParameters.map((param, idx) => (
                                        <tr key={idx}>
                                          <td
                                            style={{
                                              border: "1px solid #e5e7eb",
                                              padding: "3px 6px",
                                              fontSize: "0.65rem",
                                              fontWeight: 500,
                                              color: "#374151",
                                              backgroundColor: "#fafafa",
                                            }}
                                          >
                                            {param.name}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #e5e7eb",
                                              padding: "3px 6px",
                                              fontSize: "0.65rem",
                                              color: "#6b7280",
                                              fontFamily: "monospace",
                                            }}
                                          >
                                            {param.value}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div style={{ fontSize: "0.7rem", color: "#9ca3af", textAlign: "center" }}>-</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "issues" && (
              <IssuesTab
                issues={relatedIssues}
                isLoading={isLoadingIssues}
                error={issuesError}
                onRetry={fetchRelatedIssues}
                workspaceId={workspaceId}
                projectId={projectId}
                webBaseUrl={apiConfig?.webBaseUrl}
                completedStatusIds={completedStatusIds}
                statusMap={issueStatusMap}
              />
            )}
          </TabContent>
        </ModalBody>

        {mode !== "view" && (
          <ModalFooter>
            {hasChanges() && (
              <ChangeWarningText>
                ⚠️内容が変更されています
              </ChangeWarningText>
            )}
            <Button variant="secondary" onClick={handleCancel}>
              <CancelIcon />
              キャンセル
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                  保存中...
                </>
              ) : (
                <>
                  <SaveIcon />
                  保存
                </>
              )}
            </Button>
          </ModalFooter>
        )}

        {/* TestTargetDetailView */}
        {isTestTargetDetailOpen && (
          <TestTargetDetailView
            open={isTestTargetDetailOpen}
            onClose={handleTestTargetClose}
            onSave={handleTestTargetSave}
            testTarget={testTargetToEdit}
            testTargets={localTestTargets}
            initialMode={testTargetToEdit?.id ? "view" : "create"}
            zIndex={3200}
          />
        )}

        {/* OperationLogImportDialog（ユースケースステップ生成モード） */}
        {apiConfig && workspaceId && projectId && (
          <OperationLogImportDialog
            open={isOperationLogDialogOpen}
            onClose={() => setIsOperationLogDialogOpen(false)}
            onResultsGenerated={handleUseCaseStepsGenerated}
            workspaceId={workspaceId}
            projectId={projectId}
            operationLogService={operationLogService}
            viewId={currentViewId}
            viewName={viewName}
            mode="usecase"
            useCaseId={editedUseCase?.usecase_id}
            useCaseTitle={editedUseCase?.title || ""}
            useCaseDescription={editedUseCase?.description || ""}
            targetItemsCount={targetItemsCount}
            appSettings={appSettings}
          />
        )}

        {/* 一括更新ダイアログ */}
        {showBulkUpdateDialog && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowBulkUpdateDialog(false);
              }
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "8px",
                padding: "24px",
                maxWidth: "600px",
                width: "90%",
                maxHeight: "80vh",
                overflow: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "12px",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>
                  テストケース一括更新
                </h3>
                <button
                  onClick={() => setShowBulkUpdateDialog(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CloseIcon />
                </button>
              </div>

              <div style={{ marginBottom: "16px", fontSize: "0.85rem", color: "#6b7280" }}>
                選択した <strong>{selectedTestCaseIds.size}</strong> 件のテストケースを一括更新します
              </div>

              {/* 更新タイプ選択 */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  更新タイプ <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="bulkUpdateType"
                      value="usecase"
                      checked={bulkUpdateType === "usecase"}
                      onChange={(e) => setBulkUpdateType(e.target.value as any)}
                      style={{ marginRight: "6px" }}
                    />
                    <span style={{ fontSize: "0.85rem" }}>ユースケース・ポリシー変更</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="bulkUpdateType"
                      value="parameter"
                      checked={bulkUpdateType === "parameter"}
                      onChange={(e) => setBulkUpdateType(e.target.value as any)}
                      style={{ marginRight: "6px" }}
                    />
                    <span style={{ fontSize: "0.85rem" }}>パラメータ変更</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="bulkUpdateType"
                      value="ai_status"
                      checked={bulkUpdateType === "ai_status"}
                      onChange={(e) => setBulkUpdateType(e.target.value as any)}
                      style={{ marginRight: "6px" }}
                    />
                    <span style={{ fontSize: "0.85rem" }}>テスト準備をAIに依頼</span>
                  </label>
                </div>
              </div>

              {/* ユースケース変更の場合 */}
              {bulkUpdateType === "usecase" && (
                <>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  変更先ユースケース <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  value={bulkUpdateUsecaseId}
                  onChange={(e) => handleUsecaseChange(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "0.85rem",
                    background: "white",
                  }}
                >
                  <option value="">ユースケースを選択...</option>
                  {allUseCases.map((uc) => (
                    <option key={uc.usecase_id} value={uc.usecase_id}>
                      {uc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  変更先ポリシー（オプション）
                </label>
                <select
                  value={bulkUpdatePolicyId}
                  onChange={(e) => setBulkUpdatePolicyId(e.target.value)}
                  disabled={availablePolicies.length === 0}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "0.85rem",
                    background: availablePolicies.length === 0 ? "#f3f4f6" : "white",
                    cursor: availablePolicies.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="">ポリシーなし</option>
                  {availablePolicies.map((policy) => (
                    <option key={policy.policy_id} value={policy.policy_id}>
                      {policy.name}
                    </option>
                  ))}
                </select>
                {availablePolicies.length === 0 && bulkUpdateUsecaseId && (
                  <div style={{ marginTop: "6px", fontSize: "0.75rem", color: "#6b7280" }}>
                    選択したユースケースにはポリシーが登録されていません
                  </div>
                )}
              </div>
              </>
              )}

              {/* パラメータ変更の場合 */}
              {bulkUpdateType === "parameter" && (
                <>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        marginBottom: "8px",
                        color: "#374151",
                      }}
                    >
                      パラメータ名 <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={bulkUpdateParameterName}
                      onChange={(e) => setBulkUpdateParameterName(e.target.value)}
                      placeholder="例: browser"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "0.85rem",
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        marginBottom: "8px",
                        color: "#374151",
                      }}
                    >
                      パラメータ値 <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={bulkUpdateParameterValue}
                      onChange={(e) => setBulkUpdateParameterValue(e.target.value)}
                      placeholder="例: chrome"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "0.85rem",
                      }}
                    />
                  </div>
                </>
              )}

              {/* テスト準備をAIに依頼の場合 */}
              {bulkUpdateType === "ai_status" && (
                <div style={{ marginBottom: "16px", padding: "12px", background: "#f3f4f6", borderRadius: "4px" }}>
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#374151" }}>
                    選択したテストケースのAIステータスを「テスト準備をAIに依頼」に変更します。
                  </p>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "flex-end",
                  marginTop: "24px",
                  paddingTop: "16px",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <button
                  onClick={() => setShowBulkUpdateDialog(false)}
                  disabled={isBulkOperationLoading}
                  style={{
                    padding: "8px 16px",
                    fontSize: "0.85rem",
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isBulkOperationLoading ? "not-allowed" : "pointer",
                    opacity: isBulkOperationLoading ? 0.6 : 1,
                  }}
                >
                  キャンセル
                </button>
                <button
                  onClick={handleBulkUpdateTestCases}
                  disabled={isBulkOperationLoading}
                  style={{
                    padding: "8px 16px",
                    fontSize: "0.85rem",
                    background: isBulkOperationLoading ? "#9ca3af" : "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isBulkOperationLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    opacity: isBulkOperationLoading ? 0.6 : 1,
                  }}
                >
                  {isBulkOperationLoading ? (
                    <CircularProgress size={14} style={{ color: "white" }} />
                  ) : (
                    <SaveIcon style={{ fontSize: "0.9rem" }} />
                  )}
                  更新
                </button>
              </div>
            </div>
          </div>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};
